'use client'
import React, { useState, useRef } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { CategoryData, EventResponse, Merchant, Promotion } from '@/services/dataTypes'
import { getBaseImageUrl, getEventsHref, getMerchantHref, getPromotionHref } from '@/constants/hooks'
import SearchBar from './SearchBar'
import Image from 'next/image'
import NavbarStrip from './NavbarStrip'
import { Suspense } from 'react'

interface NavbarSecProps {
  merchantData: Merchant[],
  categories: CategoryData[] | any,
  headerPromoMerchant: Merchant[] | null,
  companyDomain: string,
  unique_id: string,
  mer_slug: string,
  mer_slug_type: string,
  promo_slug: string,
  cat_slug: string,
  blog_url?: string,
  blog_title?: string,
  company_logo: string | null,
  events: EventResponse[],
  promotions: Promotion[],
}

const NavbarSec: React.FC<NavbarSecProps> = ({
  unique_id, merchantData, categories, headerPromoMerchant, companyDomain, mer_slug, mer_slug_type, cat_slug, blog_url, blog_title, company_logo, events, promotions, promo_slug
}) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null)
  const [mobileActiveDropdown, setMobileActiveDropdown] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')

  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const handleMouseEnter = (name: string) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    setActiveDropdown(name)
  }

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setActiveDropdown(null)
    }, 200) // 200ms delay
  }
  const [mobileSearchResults, setMobileSearchResults] = useState<{ merchants: any[], categories: any[] }>({ merchants: [], categories: [] })
  const [isSearching, setIsSearching] = useState(false)
  const companyLogo = getBaseImageUrl(companyDomain, company_logo, "/shared-assets/default-dark-logo.png");

  const pathname = usePathname()

  const isActive = (href: string) => {
    if (href === '/') return pathname === '/'
    return pathname.startsWith(href.replace('/A', ''))
  }

  const navItems = [
    { name: 'Home', href: '/' },
    { name: 'Categories', href: '/category', hasDropdown: true },
    { name: 'Stores', href: '/all-stores/A', hasDropdown: true },
    { name: 'Products', href: '/all-products', hasDropdown: true },
    ...(promotions?.length > 0 ? [{ name: 'Promotions', href: `/${promo_slug}`, hasDropdown: true }] : []),
    ...(events?.length > 0 ? [{ name: 'Events', href: '/events', hasDropdown: true }] : []),
    ...(blog_title && blog_url ? [{ name: blog_title, href: blog_url, isExternal: /^https?:\/\//i.test(blog_url) }] : [])
  ]

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (searchQuery.trim()) {
      window.location.href = `/search?query=${encodeURIComponent(searchQuery.trim())}`
    }
  }

  const handleMobileSearch = async (query: string) => {
    setSearchQuery(query)
    if (query.trim().length >= 3) {
      setIsSearching(true)
      try {
        const { apiSearchResult } = await import('@/apis/user')
        const response = await apiSearchResult(query, unique_id)
        if (response.data) {
          setMobileSearchResults({
            merchants: response.data.merchants || [],
            categories: response.data.categories || []
          })
        }
      } catch (err) {
        console.error('Search error:', err)
      }
      setIsSearching(false)
    } else {
      setMobileSearchResults({ merchants: [], categories: [] })
    }
  }

  const renderSubmenu = (key: string) => {
    if (key === 'Categories') {
      return renderCategories()
    } else if (key === 'Stores') {
      return renderMerchants()
    } else if (key === 'Products') {
      return renderProducts()
    } else if (key === 'Events') {
      return renderEvents()
    } else if (key === 'Promotions') {
      return renderPromotions()
    }
    return null
  }

  // Render Categories Section
  const renderCategories = () => {
    return (
      <div className="w-[320px] rounded-[2rem] border border-white/80 bg-white/95 backdrop-blur-3xl shadow-[0_20px_40px_rgba(0,0,0,0.08)] overflow-hidden p-2">
        <div className="px-4 py-3 mb-2">
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Popular Categories</p>
        </div>

        <div className="max-h-[300px] overflow-y-auto px-2 space-y-1 scrollbar-hide">
          {categories?.length > 0 ? (
            categories.slice(0, 6).map((category: any, index: number) => (
              <Link
                key={index}
                href={category?.url?.startsWith('/') ? category.url : `/${category?.url || ''}`}
                className="block px-4 py-3 text-sm font-semibold text-slate-600 rounded-xl transition-all duration-300 hover:bg-indigo-50 hover:text-indigo-600 hover:scale-[1.02]"
              >
                {category.name}
              </Link>
            ))
          ) : (
            <div className="p-4 text-xs text-slate-400 font-medium">No categories available</div>
          )}
        </div>

        <div className="px-3 pt-3 mt-2 border-t border-slate-100">
          <Link href="/category" className="block w-full text-center py-3 text-xs font-bold text-slate-600 bg-slate-50 hover:bg-indigo-600 hover:text-white rounded-xl transition-all duration-300">
            View All Categories
          </Link>
        </div>
      </div>
    );
  };

  // Render Merchants Section
  const renderMerchants = () => {
    return (
      <div className="w-[320px] rounded-[2rem] border border-white/80 bg-white/95 backdrop-blur-3xl shadow-[0_20px_40px_rgba(0,0,0,0.08)] overflow-hidden p-2">
        <div className="px-4 py-3 mb-2">
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Popular Stores</p>
        </div>

        <div className="max-h-[320px] overflow-y-auto px-2 space-y-1 scrollbar-hide">
          {merchantData?.length > 0 ? (
            merchantData?.slice(0, 6).map((merchant, index) => (
              <Link
                key={index}
                href={getMerchantHref(merchant, mer_slug, mer_slug_type)}
                className="flex items-center gap-3 px-4 py-2 text-sm font-semibold text-slate-600 rounded-xl transition-all duration-300 hover:bg-indigo-50 hover:text-indigo-600 hover:scale-[1.02]"
              >
                <div className="relative h-10 w-10 flex-shrink-0 overflow-hidden rounded-xl border border-slate-100 bg-white">
                  <Image
                    src={getBaseImageUrl(companyDomain, merchant?.merchant_logo, "/shared-assets/default-dark-logo.png")}
                    alt={merchant?.merchant_name || 'Store Logo'}
                    fill
                    sizes="24px"
                    className="object-contain p-1"
                  />
                </div>
                <span>{merchant?.merchant_name}</span>
              </Link>
            ))
          ) : (
            <div className="p-4 text-xs text-slate-400 font-medium">No stores available</div>
          )}
        </div>

        <div className="px-3 pt-3 mt-2 border-t border-slate-100">
          <Link href="/all-stores/A" className="block w-full text-center py-3 text-xs font-bold text-slate-600 bg-slate-50 hover:bg-indigo-600 hover:text-white rounded-xl transition-all duration-300">
            View All Stores
          </Link>
        </div>
      </div>
    );
  };

  // Render Products Section
  const renderProducts = () => {
    return (
      <div className="w-[260px] rounded-[2rem] border border-white/80 bg-white/95 backdrop-blur-3xl shadow-[0_20px_40px_rgba(0,0,0,0.08)] overflow-hidden p-2">
        <div className="px-4 py-3 mb-1">
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Products</p>
        </div>

        <div className="px-2">
          <Link
            href="/products"
            className="block px-4 py-3 text-sm font-semibold text-slate-700 rounded-xl transition-all duration-300 hover:bg-indigo-50 hover:text-indigo-600 hover:scale-[1.02]"
          >
            Branded Products
          </Link>
        </div>

        <div className="px-3 pt-3 mt-2 border-t border-slate-100">
          <Link href="/all-products" className="block w-full text-center py-3 text-xs font-bold text-slate-600 bg-slate-50 hover:bg-indigo-600 hover:text-white rounded-xl transition-all duration-300">
            View All Products
          </Link>
        </div>
      </div>
    );
  };

  // Render Promotions Section
  const renderPromotions = () => {
    return (
      <div className="w-[320px] rounded-[2rem] border border-white/80 bg-white/95 backdrop-blur-3xl shadow-[0_20px_40px_rgba(0,0,0,0.08)] overflow-hidden p-2">
        <div className="px-4 py-3 mb-2">
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Popular Promotions</p>
        </div>

        <div className="max-h-[320px] overflow-y-auto px-2 space-y-1 scrollbar-hide">
          {promotions?.length > 0 ? (
            promotions?.slice(0, 8).map((promotion, index) => (
              <Link
                key={index}
                href={getPromotionHref(promotion, promo_slug)}
                className="block px-4 py-3 text-sm font-semibold text-slate-600 rounded-xl transition-all duration-300 hover:bg-indigo-50 hover:text-indigo-600 hover:scale-[1.02]"
              >
                {promotion?.name || 'Seasonal Deals'}
              </Link>
            ))
          ) : (
            <div className="p-4 text-xs text-slate-400 font-medium">No active promotions</div>
          )}
        </div>

        <div className="px-3 pt-3 mt-2 border-t border-slate-100">
          <Link href={`/${promo_slug || 'promotions'}`} className="block w-full text-center py-3 text-xs font-bold text-slate-600 bg-slate-50 hover:bg-indigo-600 hover:text-white rounded-xl transition-all duration-300">
            View All Promotions
          </Link>
        </div>
      </div>
    );
  };

  // Render Events Section
  const renderEvents = () => {
    if (!events?.length) return null;

    return (
      <div className="w-[320px] rounded-[2rem] border border-white/80 bg-white/95 backdrop-blur-3xl shadow-[0_20px_40px_rgba(0,0,0,0.08)] overflow-hidden p-2">
        <div className="px-4 py-3 mb-2">
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Popular Events</p>
        </div>

        <div className="max-h-[320px] overflow-y-auto px-2 space-y-1 scrollbar-hide">
          {events.slice(0, 8).map((event, index) => (
            <Link
              key={index}
              href={getEventsHref(event, mer_slug_type)}
              className="block px-4 py-3 text-sm font-semibold text-slate-600 rounded-xl transition-all duration-300 hover:bg-indigo-50 hover:text-indigo-600 hover:scale-[1.02]"
            >
              {event?.name}
            </Link>
          ))}
        </div>

        <div className="px-3 pt-3 mt-2 border-t border-slate-100">
          <Link href="/events" className="block w-full text-center py-3 text-xs font-bold text-slate-600 bg-slate-50 hover:bg-indigo-600 hover:text-white rounded-xl transition-all duration-300">
            View All Events
          </Link>
        </div>
      </div>
    );
  };

  return (
    <>
      <NavbarStrip
        headerPromoMerchant={headerPromoMerchant}
        companyDomain={companyDomain}
        mer_slug={mer_slug}
        mer_slug_type={mer_slug_type}
      />
      <nav className="bg-white/80 backdrop-blur-2xl sticky top-0 z-50 border-b border-slate-100 shadow-[0_4px_30px_rgba(0,0,0,0.02)] transition-all duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-[4.5rem] gap-8">

            {/* Logo Area */}
            <Link href="/" aria-label="Go to homepage" className="-ml-1 sm:-ml-2 flex flex-shrink-0 items-center">
              <Image
                className="h-10 w-auto object-contain"
                src={companyLogo}
                alt={companyDomain ? `${companyDomain} Logo` : "Site Logo"}
                width={160}
                height={40}
                sizes="160px"
                priority
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-2">
              {navItems.map((item) => (
                <div key={item.name} className="relative group">
                  {item.hasDropdown ? (
                    <div
                      className="relative"
                      onMouseEnter={() => handleMouseEnter(item.name)}
                      onMouseLeave={handleMouseLeave}
                    >
                      <button
                        type="button"
                        className={`flex items-center gap-1.5 px-4 py-2.5 text-[13px] font-bold rounded-xl transition-all duration-300 ${isActive(item.href) ? 'bg-indigo-50 text-indigo-600' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                          }`}
                      >
                        {item.name}
                        <svg
                          className={`w-3.5 h-3.5 transition-transform duration-300 ${activeDropdown === item.name ? 'rotate-180 text-indigo-600' : 'text-slate-400'}`}
                          fill="none" stroke="currentColor" viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>

                      <div className={`absolute left-1/2 -translate-x-1/2 mt-2 pt-2 pb-4 transition-all duration-300 origin-top ${activeDropdown === item.name ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 -translate-y-2 pointer-events-none'
                        }`}>
                        {renderSubmenu(item.name)}
                      </div>
                    </div>
                  ) : (
                    <Link
                      href={item.href}
                      {...(item.isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                      className={`block px-4 py-2.5 text-[13px] font-bold rounded-xl transition-all duration-300 ${isActive(item.href) ? 'bg-indigo-50 text-indigo-600' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                        }`}
                    >
                      {item.name}
                    </Link>
                  )}
                </div>
              ))}
            </div>

            {/* Search Bar Integration */}
            <div className="flex-1 max-w-xs hidden lg:block">
              <Suspense fallback={<div className="w-full h-10 bg-slate-100 rounded-xl animate-pulse" />}>
                <SearchBar
                  companyId={unique_id}
                  mer_slug={mer_slug}
                  slug_type={mer_slug_type}
                  cat_slug={cat_slug}
                  domain={companyDomain}
                />
              </Suspense>
            </div>

            {/* Mobile menu button */}
            <div className="lg:hidden flex items-center gap-2">
              <button
                type="button"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 text-slate-600 hover:bg-slate-50 rounded-xl transition-colors"
                aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
              >
                {isMobileMenuOpen ? (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                ) : (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation Panel */}
        <div className={`lg:hidden transition-all duration-300 bg-white border-t border-slate-50 ${isMobileMenuOpen ? 'max-h-[80vh] overflow-y-auto pb-6 scrollbar-hide' : 'max-h-0 overflow-hidden'}`}>
          <div className="px-4 py-4">
            <form onSubmit={handleSearch} className="relative mb-4">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => handleMobileSearch(e.target.value)}
                placeholder="Search stores or coupons..."
                className="w-full bg-slate-50 border border-slate-100 pl-4 pr-12 py-2.5 sm:py-3 rounded-xl text-base sm:text-sm text-[16px] text-slate-800 focus:bg-white focus:border-indigo-100 focus:outline-none transition-all outline-none font-medium"
              />
              <button
                type="submit"
                aria-label="Search"
                className="absolute right-1.5 top-1/2 -translate-y-1/2 w-8 h-8 sm:w-9 sm:h-9 bg-slate-800 hover:bg-indigo-400 active:scale-95 text-white rounded-xl transition-all duration-300 flex items-center justify-center shadow-[0_4px_14px_rgba(15,23,42,0.25)] hover:shadow-[0_6px_18px_rgba(79,70,229,0.4)] cursor-pointer group"
              >
                <svg className="w-4 h-4 text-white group-hover:scale-110 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="7" />
                  <line x1="21" y1="21" x2="16.5" y2="16.5" />
                </svg>
              </button>
            </form>
            {(mobileSearchResults.merchants.length > 0 || mobileSearchResults.categories.length > 0) && (
              <div className="mb-4 p-3 sm:p-4 bg-slate-50 rounded-xl max-h-64 overflow-y-auto scrollbar-hide">
                {mobileSearchResults.merchants.length > 0 && (
                  <div className="mb-3">
                    <h5 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Stores</h5>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                      {mobileSearchResults.merchants.slice(0, 5).map((merchant: any, idx: number) => (
                        <Link
                          key={idx}
                          href={getMerchantHref(merchant, mer_slug, mer_slug_type)}
                          className="flex items-center gap-2.5 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-white hover:text-indigo-600 rounded-lg transition-colors"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          <div className="relative h-6 w-6 flex-shrink-0 overflow-hidden rounded border border-slate-100 bg-white">
                            <Image
                              src={getBaseImageUrl(companyDomain, merchant?.merchant_logo, "/shared-assets/default-dark-logo.png")}
                              alt={merchant?.merchant_name || 'Store Logo'}
                              fill
                              sizes="24px"
                              className="object-contain p-0.5"
                            />
                          </div>
                          <span className="truncate">{merchant.merchant_name}</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
                {mobileSearchResults.categories.length > 0 && (
                  <div>
                    <h5 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Categories</h5>
                    <div className="flex flex-wrap gap-1.5 sm:gap-2">
                      {mobileSearchResults.categories.slice(0, 5).map((category: any, idx: number) => (
                        <Link
                          key={idx}
                          href={category?.url?.startsWith('/') ? category.url : `/${category?.url || ''}`}
                          className="px-2.5 py-1 bg-white text-slate-700 hover:bg-indigo-100 hover:text-indigo-700 rounded-lg text-xs font-semibold transition-colors"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          {category.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            <div className="space-y-1">
              {navItems.map((item) => (
                <div key={item.name}>
                  {item.hasDropdown ? (
                    <div>
                      <button
                        onClick={() => setMobileActiveDropdown(mobileActiveDropdown === item.name ? null : item.name)}
                        className="w-full flex items-center justify-between px-4 py-3 text-base font-bold text-slate-700 hover:bg-indigo-50 hover:text-indigo-600 rounded-xl transition-all"
                      >
                        {item.name}
                        <svg
                          className={`w-4 h-4 transition-transform ${mobileActiveDropdown === item.name ? 'rotate-180' : ''}`}
                          fill="none" stroke="currentColor" viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      {mobileActiveDropdown === item.name && (
                        <div className="pl-4 mt-1 space-y-1">
                          {item.name === 'Categories' && (
                            <>
                              {categories?.slice(0, 8).map((category: any, idx: number) => (
                                <Link
                                  key={idx}
                                  href={category?.url?.startsWith('/') ? category.url : `/${category?.url || ''}`}
                                  className="block px-4 py-2 text-sm font-medium text-slate-600 hover:text-indigo-600 rounded-lg"
                                  onClick={() => setIsMobileMenuOpen(false)}
                                >
                                  {category.name}
                                </Link>
                              ))}
                              <Link
                                href="/category"
                                className="block px-4 py-2 mb-2 text-sm font-bold text-indigo-600 hover:text-indigo-700 rounded-lg bg-indigo-50 hover:bg-indigo-100 transition-colors"
                                onClick={() => setIsMobileMenuOpen(false)}
                              >
                                View All Categories →
                              </Link>
                            </>
                          )}
                          {item.name === 'Stores' && (
                            <>
                              {merchantData?.slice(0, 6).map((merchant, idx) => (
                                <Link
                                  key={idx}
                                  href={getMerchantHref(merchant, mer_slug, mer_slug_type)}
                                  className="flex items-center gap-3 px-4 py-2 text-sm font-medium text-slate-600 hover:text-indigo-600 rounded-lg transition-colors"
                                  onClick={() => setIsMobileMenuOpen(false)}
                                >
                                  <div className="relative h-6 w-6 flex-shrink-0 overflow-hidden rounded border border-slate-100 bg-white">
                                    <Image
                                      src={getBaseImageUrl(companyDomain, merchant?.merchant_logo, "/shared-assets/default-dark-logo.png")}
                                      alt={merchant?.merchant_name || 'Store Logo'}
                                      fill
                                      sizes="24px"
                                      className="object-contain p-0.5"
                                    />
                                  </div>
                                  <span>{merchant?.merchant_name}</span>
                                </Link>
                              ))}
                              <Link
                                href="/all-stores/A"
                                className="block px-4 py-2 mb-2 text-sm font-bold text-indigo-600 hover:text-indigo-700 rounded-lg bg-indigo-50 hover:bg-indigo-100 transition-colors"
                                onClick={() => setIsMobileMenuOpen(false)}
                              >
                                View All Stores →
                              </Link>
                            </>
                          )}
                          {item.name === 'Products' && (
                            <>
                              <Link
                                href="/products"
                                className="block px-4 py-2 text-sm font-medium text-slate-600 hover:text-indigo-600 rounded-lg"
                                onClick={() => setIsMobileMenuOpen(false)}
                              >
                                Branded Products
                              </Link>
                              <Link
                                href="/all-products"
                                className="block px-4 py-2 mb-2 text-sm font-bold text-indigo-600 hover:text-indigo-700 rounded-lg bg-indigo-50 hover:bg-indigo-100 transition-colors"
                                onClick={() => setIsMobileMenuOpen(false)}
                              >
                                View All Products →
                              </Link>
                            </>
                          )}
                          {item.name === 'Promotions' && (
                            <>
                              <h5 className="text-xs font-black text-slate-900 uppercase tracking-wider mb-2 px-4"></h5>
                              {promotions?.slice(0, 6).map((promotion, idx) => (
                                <Link
                                  key={idx}
                                  href={getPromotionHref(promotion, promo_slug)}
                                  className="block px-4 py-2 text-sm font-medium text-slate-600 hover:text-indigo-600 rounded-lg"
                                  onClick={() => setIsMobileMenuOpen(false)}
                                >
                                  {promotion?.name}
                                </Link>
                              ))}
                              <Link
                                href={`/${promo_slug || 'promotions'}`}
                                className="block px-4 py-2 mb-2 text-sm font-bold text-indigo-600 hover:text-indigo-700 rounded-lg bg-indigo-50 hover:bg-indigo-100 transition-colors"
                                onClick={() => setIsMobileMenuOpen(false)}
                              >
                                View All Promotions →
                              </Link>
                            </>
                          )}
                          {item.name === 'Events' && (
                            <>
                              <h5 className="text-xs font-black text-slate-900 uppercase tracking-wider mb-2 px-4"></h5>
                              {events?.slice(0, 6).map((event, idx) => (
                                <Link
                                  key={idx}
                                  href={getEventsHref(event, mer_slug_type)}
                                  className="block px-4 py-2 text-sm font-medium text-slate-600 hover:text-indigo-600 rounded-lg"
                                  onClick={() => setIsMobileMenuOpen(false)}
                                >
                                  {event?.name}
                                </Link>
                              ))}
                              <Link
                                href="/events"
                                className="block px-4 py-2 mb-2 text-sm font-bold text-indigo-600 hover:text-indigo-700 rounded-lg bg-indigo-50 hover:bg-indigo-100 transition-colors"
                                onClick={() => setIsMobileMenuOpen(false)}
                              >
                                View All Events →
                              </Link>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <Link
                      href={item.href}
                      {...(item.isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                      className="block px-4 py-3 text-base font-bold text-slate-700 hover:bg-indigo-50 hover:text-indigo-600 rounded-xl transition-all"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {item.name}
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </nav>
    </>
  )
}

export default NavbarSec;
